import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getEnrollmentStatusThunk } from '@spike/payments-action';
import { EnrollmentStatus } from '@spike/payments-model';
import {
    deactivateSubscriptionThunk,
    fetchSubscriptionsThunk,
    purgeSubscriptionsThunk
} from 'actions/subscriptions/SubscriptionsActions';
import { getSelectedSettingsMarketplaceId } from 'api/settingsMarketplaceSelection';
import { ConfirmDialog, OverFullWindow, Spinner } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import { useSettingsApiClientWrapper, usePremiumPlan } from 'hooks';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { BusinessSettingsTitle } from '../UI';
import { PaymentsFlyer } from '../UI/PaymentsFlyer';
import { CurrentPlanCard } from './Cards/CurrentPlanCard';
import { DowngradePlanForm } from './Downgrade/DowngradePlanForm';
import { DowngradeSuccess } from './Downgrade/DowngradeSuccess';
import styles from './Plans.module.scss';
import { UpgradePlanForm } from './Upgrade/UpgradePlanForm';
import { UpgradeSuccess } from './Upgrade/UpgradeSuccess';

export interface Props {
    openSidebar?: () => void;
    isMobile?: boolean;
}

const statuses = {
    Initial: 'initial',

    Upgrade: 'upgrade',
    UpgradeSuccess: 'upgrade-success',

    Downgrade: 'downgrade',
    DowngradeSuccess: 'downgrade-success'
} as const;

type Status = (typeof statuses)[keyof typeof statuses];

export const Plans: React.FunctionComponent<Props> = ({ openSidebar, isMobile }) => {
    const dispatch = useDispatch();
    const activeSubscription = usePremiumPlan();
    const apiClientWrapper = useSettingsApiClientWrapper();

    const [status, setStatus] = useState<Status>(statuses.Initial);

    const [showConfirmDowngrade, setShowConfirmDowngrade] = useState(false);

    const loading = useSelector<RootState, boolean>(state => state.subscriptions.loading);

    const entrollmentStatus = useSelector<RootState, EnrollmentStatus | undefined>(
        state => state.payments.enrollmentStatus
    );

    /**
     * Fetch subscriptions
     */
    const fetch = async () => dispatch(fetchSubscriptionsThunk());

    /**
     * Upgrade plan handler
     */
    const upgradeHandler = () => {
        setStatus(statuses.UpgradeSuccess);
    };

    /**
     * Show upgrade handler
     */
    const showUpgradeHandler = () => {
        dispatch(purgeSubscriptionsThunk());
        setStatus(statuses.Upgrade);
    };

    /**
     * Downgrade plan handler
     */
    const downgradeHandler = async () => {
        if (!activeSubscription?.id) {
            return;
        }

        await dispatch(deactivateSubscriptionThunk(activeSubscription?.id));
        setShowConfirmDowngrade(false);
        setStatus(statuses.DowngradeSuccess);
        fetch();
    };

    const closeUpgradeHandler = () => {
        setStatus(statuses.Initial);
    };

    const selectedMarketplaceId = getSelectedSettingsMarketplaceId();

    useEffect(() => {
        if (status === statuses.Initial) {
            fetch();
            dispatch(getEnrollmentStatusThunk(apiClientWrapper));
        }
        // eslint-disable-next-line
    }, [status]);

    useEffect(() => {
        dispatch(getEnrollmentStatusThunk(apiClientWrapper));
        // eslint-disable-next-line
    }, [selectedMarketplaceId]);

    return (
        <div className={styles.root}>
            <BusinessSettingsTitle>
                {isMobile && (
                    <FontAwesomeIcon
                        onClick={openSidebar}
                        icon={faArrowLeft}
                        size="2x"
                    />
                )}
                Plans
            </BusinessSettingsTitle>

            {loading && <Spinner />}

            {!loading && (
                <div className={styles.sections}>
                    <CurrentPlanCard
                        subscription={activeSubscription}
                        onUpgrade={showUpgradeHandler}
                        onDowngrade={() => setStatus(statuses.Downgrade)}
                    />

                    {<PaymentsFlyer />}
                </div>
            )}

            {(status === statuses.Upgrade || status === statuses.UpgradeSuccess) && (
                <OverFullWindow
                    title="Change your Plan"
                    className={styles.modal}
                    onClose={closeUpgradeHandler}
                >
                    {status === statuses.UpgradeSuccess && <UpgradeSuccess onClose={closeUpgradeHandler} />}

                    {status === statuses.Upgrade && (
                        <div className={styles.modalContainer}>
                            <UpgradePlanForm
                                loading={loading}
                                onUpgrade={upgradeHandler}
                            />
                        </div>
                    )}
                </OverFullWindow>
            )}

            {(status === statuses.Downgrade || status === statuses.DowngradeSuccess) && (
                <OverFullWindow
                    title="Change your Plan"
                    className={styles.modal}
                    onClose={() => setStatus(statuses.Initial)}
                >
                    <div className={styles.modalContainer}>
                        {status === statuses.DowngradeSuccess && (
                            <DowngradeSuccess onClose={() => setStatus(statuses.Initial)} />
                        )}

                        {status === statuses.Downgrade && activeSubscription && (
                            <DowngradePlanForm
                                loading={loading}
                                subscription={activeSubscription}
                                onDowngrade={() => setShowConfirmDowngrade(true)}
                            />
                        )}
                    </div>
                </OverFullWindow>
            )}

            <ConfirmDialog
                isSuccess
                title={<span className={styles.dialogTitle}>Confirm Downgrade</span>}
                question={
                    <>
                        Are you sure you want to downgrade to the <b>Free Plan</b>?
                    </>
                }
                actions={
                    <>
                        <Button
                            label="No"
                            variant="black"
                            disabled={loading}
                            onClick={() => setShowConfirmDowngrade(false)}
                            className={styles.dialogButton}
                        />
                        <Button
                            label="Yes"
                            variant="green"
                            isLoading={loading}
                            onClick={downgradeHandler}
                            className={styles.dialogButton}
                        />
                    </>
                }
                className={styles.dialog}
                open={showConfirmDowngrade}
                onCancel={() => setShowConfirmDowngrade(false)}
            />
        </div>
    );
};
